// =============================================================================
// COMPOSANT GRAPHIQUE ÉVOLUTION DU SCORE IA
// Affiche l'historique des scores IA d'un chantier avec points cliquables
// =============================================================================

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CHART_STATUS_COLORS, tooltipContainerStyle } from './chartConfig';

// =============================================================================
// TYPES
// =============================================================================

export interface HistoriqueAnalyse {
  id: string;
  score: number;
  created_at: string;
  type: 'manuelle' | 'cloture';
}

interface ScoreEvolutionChartProps {
  data: HistoriqueAnalyse[];
  onPointClick?: (analyseId: string) => void;
  className?: string;
}

// =============================================================================
// FONCTIONS UTILITAIRES
// =============================================================================

/**
 * Retourne la couleur selon le score (0-100)
 */
function getScoreColor(score: number): string {
  if (score >= 70) return CHART_STATUS_COLORS.excellent;
  if (score >= 50) return CHART_STATUS_COLORS.bon;
  if (score >= 40) return CHART_STATUS_COLORS.attention;
  return CHART_STATUS_COLORS.deficit;
}

/**
 * Retourne l'icône de tendance selon l'évolution
 */
function getTendanceIcon(premiere: number, derniere: number) {
  const diff = derniere - premiere;
  if (diff > 5) return <TrendingUp className="w-4 h-4 text-green-500" />;
  if (diff < -5) return <TrendingDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-muted-foreground" />;
}

// =============================================================================
// COMPOSANT TOOLTIP PERSONNALISÉ
// =============================================================================

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: {
      id: string;
      score: number;
      date: string;
      dateComplete: string;
      type: string;
    };
  }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const scoreColor = getScoreColor(data.score);

  return (
    <div style={tooltipContainerStyle} className="min-w-[180px]">
      <div className="flex items-center justify-between gap-4 mb-2">
        <span className="text-sm text-white/70">Score IA</span>
        <span className="text-lg font-bold" style={{ color: scoreColor }}>
          {data.score}/100
        </span>
      </div>
      <div className="text-xs text-white/50">
        {data.dateComplete}
      </div>
      <div className="text-xs text-white/40 mt-1">
        Analyse {data.type === 'cloture' ? 'de clôture' : 'manuelle'}
      </div>
      <div className="text-xs text-primary mt-2 border-t border-white/10 pt-2">
        Cliquez pour voir le détail
      </div>
    </div>
  );
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export function ScoreEvolutionChart({ data, onPointClick, className }: ScoreEvolutionChartProps) {
  // Transformer les données pour Recharts
  const chartData = useMemo(() => {
    return data.map((item) => ({
      id: item.id,
      score: item.score,
      date: format(new Date(item.created_at), 'dd MMM', { locale: fr }),
      dateComplete: format(new Date(item.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr }),
      type: item.type,
    }));
  }, [data]);

  // Calculer la tendance
  const tendance = useMemo(() => {
    if (data.length < 2) return null;
    const premier = data[0].score;
    const dernier = data[data.length - 1].score;
    const diff = dernier - premier;
    return {
      premier,
      dernier,
      diff,
      pct: premier > 0 ? ((diff / premier) * 100).toFixed(0) : '0',
    };
  }, [data]);

  // Ne pas afficher si moins de 2 analyses
  if (data.length < 2) {
    return null;
  }

  // Gestion du clic sur un point
  const handleClick = (pointData: any) => {
    if (pointData && pointData.activePayload && pointData.activePayload[0]) {
      const analyseId = pointData.activePayload[0].payload.id;
      onPointClick?.(analyseId);
    }
  };

  return (
    <Card className={cn('border-primary/20', className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="w-5 h-5 text-primary" />
            Évolution du score IA
          </CardTitle>
          {tendance && (
            <div className="flex items-center gap-2 text-sm">
              {getTendanceIcon(tendance.premier, tendance.dernier)}
              <span className={cn(
                tendance.diff > 0 ? 'text-green-500' : tendance.diff < 0 ? 'text-red-500' : 'text-muted-foreground'
              )}>
                {tendance.diff > 0 ? '+' : ''}{tendance.diff} pts
              </span>
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {data.length} analyse{data.length > 1 ? 's' : ''} • Cliquez sur un point pour voir le détail
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
            onClick={handleClick}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" />
            <XAxis
              dataKey="date"
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
            />
            <YAxis
              domain={[0, 100]}
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              tickFormatter={(value) => `${value}`}
            />
            {/* Zones colorées de référence (arrière-plan subtil) */}
            <ReferenceArea y1={0} y2={30} fill="#EF4444" fillOpacity={0.05} />
            <ReferenceArea y1={30} y2={50} fill="#F59E0B" fillOpacity={0.05} />
            <ReferenceArea y1={70} y2={100} fill="#10B981" fillOpacity={0.05} />

            {/* Ligne de référence à 30 (seuil critique) */}
            <ReferenceLine
              y={30}
              stroke={CHART_STATUS_COLORS.deficit}
              strokeDasharray="5 5"
              strokeOpacity={0.5}
            />
            {/* Ligne de référence à 50 (seuil "correct") */}
            <ReferenceLine
              y={50}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="5 5"
              strokeOpacity={0.5}
            />
            {/* Ligne de référence à 70 (seuil "bon") */}
            <ReferenceLine
              y={70}
              stroke={CHART_STATUS_COLORS.bon}
              strokeDasharray="5 5"
              strokeOpacity={0.5}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="score"
              stroke="hsl(var(--primary))"
              strokeWidth={3}
              dot={(props: any) => {
                const { cx, cy, payload } = props;
                // Bleu pour manuelles, Vert pour clôture
                const color = payload.type === 'cloture' ? '#10B981' : '#3B82F6';
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={6}
                    fill={color}
                    stroke="hsl(var(--background))"
                    strokeWidth={2}
                    style={{ cursor: 'pointer' }}
                  />
                );
              }}
              activeDot={(props: any) => {
                const { cx, cy, payload } = props;
                const color = payload.type === 'cloture' ? '#10B981' : '#3B82F6';
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={8}
                    fill={color}
                    stroke="hsl(var(--background))"
                    strokeWidth={3}
                    style={{ cursor: 'pointer' }}
                  />
                );
              }}
              animationDuration={1500}
              animationEasing="ease-out"
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Légende sous le graphique */}
        <div className="flex items-center justify-center gap-6 mt-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-blue-500" />
            <span>Analyse de suivi</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-green-500" />
            <span>Analyse de clôture</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
